import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box } from '@mui/material';

// ── brand tokens ──────────────────────────────────────────────────────────────
const G1 = '#9FE870';
const G2 = '#163300';
const G3 = '#4caf50';
const G4 = '#2e7d32';
const BG = '#f0faf0';
const W  = 480;
const H  = 300;

// ── easing ────────────────────────────────────────────────────────────────────
const eio  = t => t < 0.5 ? 2*t*t : -1+(4-2*t)*t;
const clamp = (v,lo,hi) => Math.max(lo,Math.min(hi,v));
const lerp  = (a,b,t) => a+(b-a)*t;

// ── bar data ──────────────────────────────────────────────────────────────────
const BAR_BASE   = [0.45, 0.62, 0.38, 0.78, 0.55, 0.90, 0.70];
const BAR_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul'];

// ── sparkline points ──────────────────────────────────────────────────────────
const SP_PTS = [0.6, 0.4, 0.7, 0.5, 0.8, 0.65, 0.9];
const SP2_PTS= [0.5, 0.7, 0.45, 0.8, 0.6, 0.75, 0.55];

// ── activity items (cycles through these) ────────────────────────────────────
const ACTIVITY = [
  { icon: '✓', text: 'Invoice #1042 paid',    time: '2m ago',  color: G3        },
  { icon: '↑', text: 'PO #88 approved',       time: '14m ago', color: G1        },
  { icon: '!', text: 'Low stock: SKU-204',    time: '1h ago',  color: '#f59e0b' },
  { icon: '↓', text: 'Expense #312 filed',    time: '2h ago',  color: G4        },
  { icon: '✓', text: 'Payment: $31,200',      time: 'just now',color: G3        },
  { icon: '↑', text: 'New customer: Softtns', time: '5m ago',  color: G1        },
  { icon: '!', text: 'Payroll run complete',  time: '10m ago', color: '#a78bfa' },
];

// ── stat card targets ─────────────────────────────────────────────────────────
const STATS = [
  { label: 'Total Revenue', target: 84200, prefix: '$', suffix: '',  fmt: v => `$${(v/1000).toFixed(1)}K`, sub: '↑ 12.4%', color: G3        },
  { label: 'Invoices',      target: 1248,  prefix: '',  suffix: '',  fmt: v => Math.round(v).toLocaleString(),sub:'↑ 8 today',color: G1       },
  { label: 'Expenses',      target: 21800, prefix: '$', suffix: '',  fmt: v => `$${(v/1000).toFixed(1)}K`, sub: '↓ 3.1%', color: G4        },
  { label: 'Net Profit',    target: 62400, prefix: '$', suffix: '',  fmt: v => `$${(v/1000).toFixed(1)}K`, sub: '↑ 18.7%',color: '#059669' },
];

// ── canvas renderer ───────────────────────────────────────────────────────────
function useDashCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width  = W;
    canvas.height = H;

    let raf;
    const start = performance.now();

    // state
    let activityOffset = 0;       // which 4 items to show
    let lastActivitySwap = 0;
    let toastPhase = 0;           // 0=hidden,1=entering,2=visible,3=leaving
    let toastT = 0;
    let lastToastCycle = 0;
    const TOAST_CYCLE = 5000;

    // ── helpers ───────────────────────────────────────────────────────────────
    const roundRect = (x, y, w, h, r, fill, alpha = 1) => {
      ctx.globalAlpha = alpha;
      ctx.fillStyle   = fill;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, r);
      ctx.fill();
      ctx.globalAlpha = 1;
    };

    const txt = (str, x, y, size, fill, weight = 400, alpha = 1) => {
      ctx.globalAlpha  = alpha;
      ctx.fillStyle    = fill;
      ctx.font         = `${weight} ${size}px Inter,sans-serif`;
      ctx.fillText(str, x, y);
      ctx.globalAlpha  = 1;
    };

    const line = (x1, y1, x2, y2, color, width = 1, alpha = 1) => {
      ctx.globalAlpha  = alpha;
      ctx.strokeStyle  = color;
      ctx.lineWidth    = width;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.globalAlpha  = 1;
    };

    const sparkline = (pts, x, y, w, h, color, dotPos = -1) => {
      const step = w / (pts.length - 1);
      // area
      ctx.globalAlpha = 0.15;
      ctx.fillStyle   = color;
      ctx.beginPath();
      pts.forEach((v, i) => {
        const px = x + i * step, py = y + h - v * h;
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      });
      ctx.lineTo(x + w, y + h);
      ctx.lineTo(x, y + h);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
      // line
      ctx.strokeStyle  = color;
      ctx.lineWidth    = 1.5;
      ctx.lineJoin     = 'round';
      ctx.lineCap      = 'round';
      ctx.beginPath();
      pts.forEach((v, i) => {
        const px = x + i * step, py = y + h - v * h;
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      });
      ctx.stroke();
      // travelling dot
      if (dotPos >= 0) {
        const idx  = Math.floor(dotPos * (pts.length - 1));
        const frac = dotPos * (pts.length - 1) - idx;
        const v0   = pts[idx], v1 = pts[Math.min(idx + 1, pts.length - 1)];
        const v    = lerp(v0, v1, frac);
        const dx   = x + dotPos * w;
        const dy   = y + h - v * h;
        ctx.globalAlpha = 0.9;
        ctx.fillStyle   = color;
        ctx.beginPath();
        ctx.arc(dx, dy, 3, 0, Math.PI * 2);
        ctx.fill();
        // glow
        ctx.globalAlpha = 0.25;
        ctx.beginPath();
        ctx.arc(dx, dy, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    };

    // ── draw ──────────────────────────────────────────────────────────────────
    const draw = (now) => {
      raf = requestAnimationFrame(draw);
      const t = (now - start) / 1000; // seconds elapsed

      ctx.clearRect(0, 0, W, H);

      // ── background ──────────────────────────────────────────────────────────
      roundRect(0, 0, W, H, 12, BG);

      // ── title bar ───────────────────────────────────────────────────────────
      roundRect(0, 0, W, 28, [12, 12, 0, 0], G2);
      ctx.fillStyle = G2;
      ctx.fillRect(0, 14, W, 14);
      // traffic lights
      [['#ff5f57', 14], ['#febc2e', 26], ['#28c840', 38]].forEach(([c, cx]) => {
        ctx.fillStyle = c; ctx.beginPath(); ctx.arc(cx, 14, 4, 0, Math.PI*2); ctx.fill();
      });
      txt('FluxERP', W/2 - 22, 18, 7.5, G1, 700);

      // ── sidebar ─────────────────────────────────────────────────────────────
      ctx.fillStyle = G2;
      ctx.fillRect(0, 28, 52, H - 28);
      roundRect(14, 36, 24, 24, 6, G1);
      txt('F', 19, 52, 9, G2, 800);

      const navItems = [
        { icon: '▦', label: 'Dash',    y: 76,  active: true  },
        { icon: '◈', label: 'Inv',     y: 96                 },
        { icon: '⊞', label: 'Stock',   y: 116                },
        { icon: '◉', label: 'CRM',     y: 136                },
        { icon: '⊟', label: 'Pay',     y: 156                },
        { icon: '≡', label: 'Reports', y: 176                },
      ];
      navItems.forEach(({ icon, label, y, active }) => {
        if (active) {
          roundRect(0, y - 8, 52, 20, 0, G1, 0.15);
          roundRect(0, y - 8, 3,  20, 0, G1);
        }
        txt(icon,  16, y + 5,  8,   active ? G1 : 'white', 400, active ? 1 : 0.45);
        txt(label, 10, y + 15, 4.5, active ? G1 : 'white', 400, active ? 1 : 0.35);
      });

      // ── topbar ───────────────────────────────────────────────────────────────
      roundRect(52, 28, W - 52, 22, 0, 'white', 0.6);
      txt('Dashboard', 60, 43, 7, G2, 700);
      roundRect(W - 120, 33, 70, 12, 6, BG);
      txt('Search…', W - 116, 42, 5.5, G2, 400, 0.4);
      // blinking cursor in search
      if (Math.floor(t * 1.5) % 2 === 0) {
        ctx.fillStyle = G2; ctx.globalAlpha = 0.5;
        ctx.fillRect(W - 80, 34, 1, 9);
        ctx.globalAlpha = 1;
      }
      // notification bell
      roundRect(W - 37, 32, 14, 14, 7, 'white', 0.8);
      txt('🔔', W - 36, 43, 9, G2, 400, 0.6);
      // pulsing red dot
      const dotA = 0.6 + 0.4 * Math.sin(t * 3);
      ctx.fillStyle = '#ef4444'; ctx.globalAlpha = dotA;
      ctx.beginPath(); ctx.arc(W - 24, 34, 3, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;

      // ── stat cards ──────────────────────────────────────────────────────────
      const countDur = 1.4;
      STATS.forEach(({ label, target, fmt, sub, color }, i) => {
        const cx = 58 + i * 84;
        const progress = clamp((t - 0.2 - i * 0.1) / countDur, 0, 1);
        const val = target * eio(progress);

        roundRect(cx, 56, 78, 44, 5, 'white');
        roundRect(cx, 56, 3,  44, 2, color);
        txt(label,   cx + 8, 69,  6,  G2, 400, 0.55);
        txt(fmt(val),cx + 8, 82,  10, G2, 700);
        txt(sub,     cx + 8, 94,  5.5,color,600);
      });

      // ── bar chart ────────────────────────────────────────────────────────────
      roundRect(58, 108, 170, 80, 5, 'white');
      txt('Revenue Overview', 66, 120, 6.5, G2, 700);
      txt('Last 7 months',    66, 129, 5,   G2, 400, 0.45);

      const chartX = 62, chartY = 132, chartW = 158, chartH = 48;
      line(chartX, chartY + chartH, chartX + chartW, chartY + chartH, G2, 1, 0.12);

      const bw = (chartW - (BAR_BASE.length - 1) * 4) / BAR_BASE.length;
      BAR_BASE.forEach((base, i) => {
        // each bar breathes slightly at a different phase
        const breathe = 1 + 0.04 * Math.sin(t * 0.8 + i * 0.9);
        const bh = clamp(base * breathe, 0, 1) * (chartH - 10);
        const bx = chartX + i * (bw + 4);
        const by = chartY + chartH - bh;
        const growP = clamp((t - 0.4 - i * 0.1) / 0.8, 0, 1);
        const drawH = bh * eio(growP);

        ctx.fillStyle = i === 5 ? G1 : G3;
        ctx.globalAlpha = i === 5 ? 1 : 0.55;
        ctx.beginPath();
        ctx.roundRect(bx, chartY + chartH - drawH, bw, drawH, 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      });

      // ── sparkline cards ──────────────────────────────────────────────────────
      const dotPos1 = (t * 0.18) % 1;
      const dotPos2 = (t * 0.14 + 0.5) % 1;

      roundRect(236, 108, 80, 36, 5, 'white');
      txt('Cash Flow',  242, 119, 5.5, G2, 400, 0.5);
      txt('+$14.2K',    242, 129, 8,   G2, 700);
      sparkline(SP_PTS,  236, 118, 80, 18, G1, dotPos1);

      roundRect(236, 150, 80, 36, 5, 'white');
      txt('Receivables', 242, 161, 5.5, G2, 400, 0.5);
      txt('$38.9K',      242, 171, 8,   G2, 700);
      sparkline(SP2_PTS, 236, 160, 80, 18, G4, dotPos2);

      // ── activity feed ────────────────────────────────────────────────────────
      // swap top item every 2.5 s
      if (now - lastActivitySwap > 2500) {
        activityOffset = (activityOffset + 1) % ACTIVITY.length;
        lastActivitySwap = now;
      }

      roundRect(322, 108, 150, 80, 5, 'white');
      txt('Recent Activity', 330, 120, 6.5, G2, 700);

      for (let i = 0; i < 4; i++) {
        const item = ACTIVITY[(activityOffset + i) % ACTIVITY.length];
        const iy   = 132 + i * 15;
        // fade in the newest item (i===0)
        const itemAge = (now - lastActivitySwap) / 400;
        const alpha   = i === 0 ? clamp(itemAge, 0, 1) : 1;

        ctx.globalAlpha = alpha * 0.2;
        ctx.fillStyle   = item.color;
        ctx.beginPath(); ctx.arc(334, iy, 5, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;

        txt(item.icon, 331, iy + 3,  5.5, item.color, 700, alpha);
        txt(item.text, 342, iy + 3,  5.5, G2,         400, alpha * 0.75);
        txt(item.time, 440, iy + 3,  5,   G2,         400, alpha * 0.35);
      }

      // ── invoice table ────────────────────────────────────────────────────────
      const tableRows = [
        { num: '#INV-1042', client: 'Dutco Group',    amount: '$12,400', status: 'Paid',    sc: G3        },
        { num: '#INV-1041', client: 'Core Media LLC', amount: '$8,750',  status: 'Pending', sc: '#f59e0b' },
        { num: '#INV-1040', client: 'NSSTC Holdings', amount: '$31,200', status: 'Paid',    sc: G3        },
        { num: '#INV-1039', client: 'Teevision Print',amount: '$5,600',  status: 'Overdue', sc: '#ef4444' },
      ];

      roundRect(58, 196, 414, 14, 3, G2, 0.06);
      txt('INVOICE #', 64,  206, 5.5, G2, 700, 0.5);
      txt('CLIENT',    104, 206, 5.5, G2, 700, 0.5);
      txt('AMOUNT',    164, 206, 5.5, G2, 700, 0.5);
      txt('STATUS',    430, 206, 5.5, G2, 700, 0.5);

      tableRows.forEach(({ num, client, amount, status, sc }, i) => {
        const rowT = clamp((t - 0.7 - i * 0.08) / 0.4, 0, 1);
        const alpha = eio(rowT);
        roundRect(58, 212 + i * 18, 414, 16, 3, 'white', alpha * 0.7);
        txt(num,    64,  222 + i * 18, 6,   G2, 600, alpha);
        txt(client, 104, 222 + i * 18, 6,   G2, 400, alpha * 0.7);
        txt(amount, 164, 222 + i * 18, 6,   G2, 600, alpha);
        roundRect(430 - 4, 215 + i * 18, 34, 10, 5, sc, alpha * 0.15);
        txt(status, 430, 223 + i * 18, 5.5, sc, 700, alpha);
      });

      // ── toast notification ───────────────────────────────────────────────────
      const elapsed = now - lastToastCycle;
      if (elapsed > TOAST_CYCLE) { toastPhase = 1; toastT = 0; lastToastCycle = now; }

      if (toastPhase === 1) {
        toastT += 16 / 400;
        if (toastT >= 1) { toastT = 1; toastPhase = 2; lastToastCycle = now; }
      } else if (toastPhase === 2 && now - lastToastCycle > 2200) {
        toastPhase = 3; toastT = 1;
      } else if (toastPhase === 3) {
        toastT -= 16 / 350;
        if (toastT <= 0) { toastT = 0; toastPhase = 0; }
      }

      if (toastPhase > 0) {
        const ta = eio(clamp(toastT, 0, 1));
        const ty = lerp(8, 0, ta);
        ctx.save();
        ctx.translate(0, ty);
        ctx.globalAlpha = ta;
        // shadow
        ctx.shadowColor  = 'rgba(22,51,0,0.18)';
        ctx.shadowBlur   = 12;
        ctx.shadowOffsetY= 4;
        roundRect(W - 148, H - 52, 138, 38, 7, 'white');
        ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
        roundRect(W - 148, H - 52, 4, 38, 2, G1);
        ctx.globalAlpha = ta;
        txt('Payment Received',          W - 140, H - 37, 6,   G2, 700);
        txt('$12,400 from Dutco Group',  W - 140, H - 26, 5.5, G2, 400, 0.55);
        // pulsing dot
        const pd = 0.6 + 0.4 * Math.sin(t * 4);
        ctx.fillStyle = G1; ctx.globalAlpha = ta * pd;
        ctx.beginPath(); ctx.arc(W - 18, H - 44, 5, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
        ctx.globalAlpha = 1;
      }

      // ── glare overlay ────────────────────────────────────────────────────────
      const glare = ctx.createLinearGradient(0, 0, W * 0.4, H);
      glare.addColorStop(0,   'rgba(255,255,255,0.06)');
      glare.addColorStop(1,   'rgba(255,255,255,0)');
      ctx.fillStyle   = glare;
      ctx.globalAlpha = 1;
      ctx.beginPath();
      ctx.roundRect(0, 0, W, H, 12);
      ctx.fill();
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef]);
}

// ── component ─────────────────────────────────────────────────────────────────
const DashboardMockup = () => {
  const canvasRef = useRef(null);
  useDashCanvas(canvasRef);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 520,
        position: 'relative',
        filter: 'drop-shadow(0 24px 48px rgba(22,51,0,0.22))',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: 'auto', borderRadius: 12 }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: '-1px',
          borderRadius: '13px',
          border: '1px solid rgba(22,51,0,0.12)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default DashboardMockup;
